import { graphql, Link } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import { Text, TextColor, TextVariant } from 'hem-ui';
import React from 'react';

import { fixSlug } from '../RichText/RichText';
import * as styles from './ArticlePreview.module.scss';

export const ArticlePreview: React.FC<
  Queries.ArticlePreviewFragment & {
    titleTag?: keyof JSX.IntrinsicElements;
    titleVariant?: TextVariant;
    // used to fix layout when carousel children have container inline-size
    displayContents?: boolean;
  }
> = ({
  slug,
  excerpt,
  featuredImage,
  title,
  titleTag = 'p',
  titleVariant = TextVariant.HEADLINE_SECTION,
  displayContents = false,
}): JSX.Element => {
  const cleanExcerpt = (excerpt?.childMarkdownRemark?.excerpt || '').replace(
    /(<([^>]+)>)/gi,
    '',
  );

  return (
    <div
      className={styles.container}
      style={displayContents ? { display: 'contents' } : undefined}
    >
      <Link
        to={fixSlug(slug)}
        className={['group', styles.root].join(' ')}
        aria-label={`${title}. Read more`}
        data-selector="PDP Related content link"
      >
        {!!featuredImage?.gatsbyImageData && (
          <GatsbyImage
            image={featuredImage.gatsbyImageData}
            alt={title}
            className={styles.image}
          />
        )}

        <div className={styles.content}>
          <Text
            className={styles.latest}
            variant={TextVariant.TITLE_SMALL}
            color={TextColor.GRAY_DARK}
          >
            Latest from Hem
          </Text>

          <Text
            tag={titleTag}
            variant={titleVariant}
            className={styles.title}
            dangerouslySetInnerHTML={{
              __html: title,
            }}
          />

          <p
            className="mb-1 line-clamp-3 max-w-prose text-base text-black-600"
            dangerouslySetInnerHTML={{
              __html: cleanExcerpt,
            }}
          />

          <p className="text-blue text-base font-bold group-hover:underline">
            Read more
          </p>
        </div>
      </Link>
    </div>
  );
};

export const query = graphql`
  fragment ArticlePreview on ContentfulPageArticle {
    slug
    title
    excerpt {
      childMarkdownRemark {
        excerpt
      }
    }
    featuredImage {
      gatsbyImageData(
        formats: [AUTO, AVIF, WEBP]
        quality: 80
        width: 920
        aspectRatio: 1.5
        resizingBehavior: FILL
        cropFocus: FACES
        placeholder: BLURRED
      )
    }
  }
`;
